<template>
  <ExLoader v-if="!dashboardReady" text="Loading..." />
  <YourDashboardNoAssignments
    v-else-if="!hasCurrentPositionAssignment"
    :readiness="readiness"
    :last_month_readiness="last_month_readiness"
    :readiness-map="readinessMap"
    :chart-data="chartData"
    :responsibilities-count="responsibilitiesCount"
    :badges-count="badgesCount"
    :expiring-badge-count="expiringBadgeCount"
    :obsolete-badge-count="obsoleteBadgeCount"
    :days-until-expire="BADGE_ABOUT_TO_EXPIRE_WITHIN_DAYS"
    :expiring-action-label="expiringActionLabel"
    :show-expiring-badges-banner="showExpiringBadgesBanner"
    :show-obsolete-badges-banner="showObsoleteBadgesBanner"
    @show-expiring-badges="goToExpiringBadges"
    @show-obsolete-badges="goToObsoleteBadges"
  />
  <div v-else class="your-dashboard">
    <!-- Readiness Overview Row -->
    <div class="your-dashboard__overview-row">
      <div class="your-dashboard__overview-item your-dashboard__overview-item--readiness">
        <AverageReadinessBanner
          :averageReadiness="readiness"
          :last_month_averageReadiness="last_month_readiness"
          averageReadinessTitle="Your Readiness"
        />
      </div>
      <div class="your-dashboard__overview-item your-dashboard__overview-item--map">
        <YourReadinessMap
          :novice_value="readinessMap.novice"
          :apprentice_value="readinessMap.apprentice"
          :proficient_value="readinessMap.professional"
          :coach_value="readinessMap.coach"
          :architect_value="readinessMap.master"
          :countShow="false"
        />
      </div>
    </div>
    <YourAboutToExpireBadgesBanner
      v-if="showExpiringBadgesBanner"
      :expiringBadgeCount="expiringBadgeCount"
      :daysUntilExpire="BADGE_ABOUT_TO_EXPIRE_WITHIN_DAYS"
      :action-label="expiringActionLabel"
      @show-expiring-badges="goToExpiringBadges"
    />
    <YourObsoleteBadgesBanner
      v-if="showObsoleteBadgesBanner"
      :obsolete-badge-count="obsoleteBadgeCount"
      @show-obsolete-badges="goToObsoleteBadges"
    />
    <!-- Main Chart and Cards Row -->
    <div class="your-dashboard__main-row">
      <div class="your-dashboard__main-item your-dashboard__main-item--chart">
        <ReadinessEvolutionChart
          :today="chartData.today"
          :today_month_minus_1="chartData.today_month_minus_1"
          :today_month_minus_2="chartData.today_month_minus_2"
          :today_month_minus_3="chartData.today_month_minus_3"
          :today_month_minus_4="chartData.today_month_minus_4"
          :today_month_minus_5="chartData.today_month_minus_5"
          :today_month_minus_6="chartData.today_month_minus_6"
        />
      </div>
      <div class="your-dashboard__main-item your-dashboard__main-item--cards">
        <YourCountOfAssignedResponsibilitiesDashboardCard
          :assigned_responsibilities_with_apprentice_level="responsibilitiesCount.apprentice"
          :assigned_responsibilities_with_professional_level="responsibilitiesCount.professional"
          :assigned_responsibilities_with_coach_level="responsibilitiesCount.coach"
          :assigned_responsibilities_with_master_level="responsibilitiesCount.master"
          :assigned_responsibilities_with_novice_level="responsibilitiesCount.novice"
        />
        <YourCountOfCurrentBadgesDashboardCard
          :apprentice_badge_count="badgesCount.apprentice"
          :professional_badge_count="badgesCount.professional"
          :coach_badge_count="badgesCount.coach"
          :master_badge_count="badgesCount.master"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useRouter } from "vue-router/composables";
import AverageReadinessBanner from "@components/AverageReadinessBanner/AverageReadinessBanner.vue";
import YourReadinessMap from "@components/YourReadinessMap/YourReadinessMap.vue";
import ReadinessEvolutionChart from "@components/ReadinessEvolutionChart/ReadinessEvolutionChart.vue";
import YourCountOfAssignedResponsibilitiesDashboardCard from "@components/YourCountOfAssignedResponsibilitiesDashboardCard/YourCountOfAssignedResponsibilitiesDashboardCard.vue";
import YourCountOfCurrentBadgesDashboardCard from "@components/YourCountOfCurrentBadgesDashboardCard/YourCountOfCurrentBadgesDashboardCard.vue";
import YourAboutToExpireBadgesBanner from "@components/YourAboutToExpireBadgesBanner/YourAboutToExpireBadgesBanner.vue";
import YourObsoleteBadgesBanner from "@components/YourObsoleteBadgesBanner/YourObsoleteBadgesBanner.vue";
import YourDashboardNoAssignments from "@components/YourDashboardNoAssignments/YourDashboardNoAssignments.vue";
import ExLoader from "@components/common/ExLoader.vue";
import { getEmployeeById, getAuthUserProficiencyLevel, getAllEmployeeBadges, getReadinessEvolution } from "@/api";
import { employeeHasCurrentPositionAssignment } from "@/utils/employeePositionAssignment";
import {
  BADGE_ABOUT_TO_EXPIRE_WITHIN_DAYS,
  countCurrentBadgesAboutToExpire,
  countCurrentBadgesObsolete,
  getYourStuffExpiringBadgesRoute,
  getYourStuffObsoleteBadgesRoute,
} from "@/utils/badgeExpiration";
import { deriveDashboardBadgeCountsFromResults } from "@/utils/employeeBadgeListUtils";

const { proxy } = getCurrentInstance();
const router = useRouter();

/** idea-MDP-463: Your Dashboard "about to expire" badges banner. */
const ideaMDP463 = computed(
  () => proxy?.$store?.getters?.["featureFlags/ideaMDP463"] === true
);

const expiringBadgeCount = ref(0);
const obsoleteBadgeCount = ref(0);
const expiringActionLabel = ref("Show your expiring badges");

const showExpiringBadgesBanner = computed(
  () => ideaMDP463.value && expiringBadgeCount.value > 0
);

const showObsoleteBadgesBanner = computed(
  () => ideaMDP463.value && obsoleteBadgeCount.value > 0
);

function goToExpiringBadges() {
  router.push(getYourStuffExpiringBadgesRoute()).catch(() => {});
}

function goToObsoleteBadges() {
  router.push(getYourStuffObsoleteBadgesRoute()).catch(() => {});
}

const readiness = ref(null);
const last_month_readiness = ref(null);
const readinessMap = ref({ novice: 0, apprentice: 0, professional: 0, coach: 0, master: 0 });
const chartData = ref({
  today: null,
  today_month_minus_1: null,
  today_month_minus_2: null,
  today_month_minus_3: null,
  today_month_minus_4: null,
  today_month_minus_5: null,
  today_month_minus_6: null,
});
const responsibilitiesCount = ref({ novice: 0, apprentice: 0, professional: 0, coach: 0, master: 0 });
const badgesCount = ref({ apprentice: 0, professional: 0, coach: 0, master: 0 });
const dashboardReady = ref(false);
const hasCurrentPositionAssignment = ref(true);

onMounted(async () => {
  const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
  const authUser = JSON.parse(localStorage.getItem("user"));
  const empId = authUser?.employee?.id;

  if (!accountID || !empId) {
    hasCurrentPositionAssignment.value = false;
    dashboardReady.value = true;
    return;
  }

  try {
    const [employeeRes, proficiencyRes, badgesRes, weekRes, monthRes, quarterRes] = await Promise.allSettled([
      getEmployeeById(accountID, empId),
      getAuthUserProficiencyLevel(accountID, empId),
      getAllEmployeeBadges(accountID, empId, { status: "current" }),
      getReadinessEvolution(accountID, empId, "week"),
      getReadinessEvolution(accountID, empId, "month"),
      getReadinessEvolution(accountID, empId, "quarter"),
    ]);

    if (employeeRes.status === "fulfilled") {
      const emp = employeeRes.value.data;
      hasCurrentPositionAssignment.value = employeeHasCurrentPositionAssignment(emp);
      readiness.value = emp.current_readiness ?? null;
      last_month_readiness.value = emp.last_month_readiness ?? null;
    } else {
      hasCurrentPositionAssignment.value = true;
    }

    const weeks = weekRes.status === "fulfilled" ? weekRes.value.data.periods : Array(7).fill(null);
    const months = monthRes.status === "fulfilled" ? monthRes.value.data.periods : Array(7).fill(null);
    const quarters = quarterRes.status === "fulfilled" ? quarterRes.value.data.periods : Array(7).fill(null);
    const keys = ["today_month_minus_6", "today_month_minus_5", "today_month_minus_4", "today_month_minus_3", "today_month_minus_2", "today_month_minus_1", "today"];
    const merged = {};
    keys.forEach((key, i) => {
      merged[key] = {
        weeks: weeks[i]?.value ?? null,
        months: months[i]?.value ?? null,
        quarters: quarters[i]?.value ?? null,
      };
    });
    chartData.value = merged;

    if (proficiencyRes.status === "fulfilled") {
      const p = proficiencyRes.value.data;
      readinessMap.value = {
        novice: p.novice || 0,
        apprentice: p.apprentice || 0,
        professional: p.professional || 0,
        coach: p.expert || 0,
        master: p.master || 0,
      };
      responsibilitiesCount.value = {
        novice: p.novice || 0,
        apprentice: p.apprentice || 0,
        professional: p.professional || 0,
        coach: p.expert || 0,
        master: p.master || 0,
      };
    }

    if (badgesRes.status === "fulfilled") {
      const currentBadges = badgesRes.value?.results || [];
      badgesCount.value = deriveDashboardBadgeCountsFromResults(currentBadges);
      // Not gated on the flag here: LaunchDarkly resolves after mount on a reload, and the
      // show* computeds already gate the banners reactively.
      expiringBadgeCount.value = countCurrentBadgesAboutToExpire(currentBadges);
      obsoleteBadgeCount.value = countCurrentBadgesObsolete(currentBadges);
    }
  } finally {
    dashboardReady.value = true;
  }
});
</script>

<style scoped>
.your-dashboard {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background-color: transparent;
}

/* Base styles for the rows */
.your-dashboard__overview-row,
.your-dashboard__main-row {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.your-dashboard__overview-item {
  flex: 1;
}

.your-dashboard__main-item--chart {
  flex: 1;
}

.your-dashboard__main-item--cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 0 0 330px;
}

/* Sections styling: Border and Box Shadow */
.your-dashboard__overview-item--readiness :deep(.average-readiness-banner),
.your-dashboard__overview-item--map :deep(.your-readiness-map) {
  border: 1px solid #EAECF0 !important;
  box-shadow: 0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A !important;
  border-radius: 12px !important;
  background-color: #FFFFFF !important;
}

.your-dashboard__main-item--chart :deep(.readiness-evolution-chart),
.your-dashboard__main-item--cards :deep(> *) {
  border: 1px solid #EAECF0 !important;
  border-radius: 12px !important;
  background-color: #FFFFFF !important;
}

/* Consistency fixes for titles in the overview row */
.your-dashboard__overview-item--readiness :deep(.avg-readiness-bar-title-as-employee),
.your-dashboard__overview-item--map :deep(.your-readiness-map-title) {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #101828 !important;
  margin: 0 !important;
  font-family: Inter, sans-serif !important;
}

.your-dashboard__overview-item--readiness :deep(.average-readiness-banner) {
  height: 65px;
  padding: 0 24px;
  display: flex;
  align-items: center;
}

.your-dashboard__overview-item--readiness :deep(.average-readiness-banner .avg-readiness-bar-as-employee) {
  position: relative;
  top: -6px;
}

.your-dashboard__overview-item--readiness :deep(.ex-readiness-box) {
  margin-top: -12px;
}

.your-dashboard__overview-item--map :deep(.your-readiness-map) {
  height: 65px;
  padding: 0 24px;
}
</style>
